/**
 * UserJot loader — deliberately lazy, and only ever called after login.
 *
 * The SDK used to be a <script> in index.html, which meant every anonymous
 * visitor to the marketing pages loaded a third-party script that then received
 * their id, email and name once they signed in. Two problems with that:
 *
 *   1. It ran for people who had no relationship with us at all.
 *   2. A non-essential third party on the public pages is what forces a cookie
 *      consent banner. Vercel Analytics and Speed Insights are cookieless and
 *      Sentry sets no cookies, so with this moved behind login the anonymous
 *      surface sets nothing non-essential — and needs no banner.
 *
 * The proxy queues any call made before the SDK finishes downloading, so it is
 * safe to call init() immediately after install().
 */

const PROJECT_ID = 'cmnikkokh0q610io65842s968';
const SDK_URL = 'https://cdn.userjot.com/sdk/v2/uj.js';

let installed = false;

/** Only the three methods we call. The proxy accepts anything, but narrowing
 *  the type keeps call sites honest about what we actually depend on. */
type UserJot = {
  init: (id: string, opts: Record<string, unknown>) => void;
  identify: (u: Record<string, unknown>) => void;
  setTheme: (t: string) => void;
};

/** Injects the SDK and its call queue. Safe to call repeatedly. */
export function installUserJot(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const w = window as unknown as { $ujq?: unknown[]; uj?: UserJot };
  w.$ujq = w.$ujq || [];
  w.uj =
    w.uj ||
    (new Proxy({}, {
      get: (_t, prop: string) => (...args: unknown[]) => w.$ujq!.push([prop, ...args]),
    }) as UserJot);

  document.head.appendChild(
    Object.assign(document.createElement('script'), {
      src: SDK_URL,
      type: 'module',
      async: true,
    })
  );
}

/** The SDK handle, or undefined if install() has not run. Never throws. */
export function userjot(): UserJot | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { uj?: UserJot }).uj;
}

export { PROJECT_ID as USERJOT_PROJECT_ID };
